import { createContext, ReactNode, useContext } from 'react';

import { ColumnOrderState, VisibilityState } from '@tanstack/react-table';
import styled from 'styled-components';

import { noop } from '@utils/browser';

import { AvailableColumn, DataItemType, TableView } from '../types';

export type ViewsContextValue = {
  views: Array<TableView>;
  selectedView: TableView | undefined;
  createView: (newView: TableView) => void;
  updateView: (updatedView: TableView) => void;
  deleteView: (viewId: string) => void;
  selectView: (viewId: string) => void;
  reorderViews: (newOrder: Array<string>) => void;
  onToggleFavorite?: (view: TableView) => void;
  hasUnsavedChanges?: boolean;
  saveView?: () => void;
  discardChanges?: () => void;
  activeFilterConfigIds?: Set<string>;
};

const defaultViewsContextValue: ViewsContextValue = {
  views: [],
  selectedView: undefined,
  createView: noop,
  updateView: noop,
  deleteView: noop,
  selectView: noop,
  reorderViews: noop,
};

export const ViewsContext = createContext<ViewsContextValue>(defaultViewsContextValue);

export const useViews = () => {
  return useContext(ViewsContext);
};

export const getDefaultColumnState = <DataItem extends DataItemType = DataItemType>(
  availableColumns: Array<AvailableColumn<DataItem>>
) => {
  const columnOrder: ColumnOrderState = availableColumns.map((column) => column.id);
  const columnVisibility: VisibilityState = {};

  availableColumns.forEach((column) => {
    columnVisibility[column.id] = !column.defaultHidden;
  });

  return {
    columnOrder,
    columnVisibility,
  };
};

export const FiltersScrollContainer = styled.div({
  minWidth: 0,
  flex: 1,
  overflowX: 'auto',
});

export type ViewsProviderProps = ReturnType<typeof useViews> & {
  children: ReactNode;
};

export const ViewsProvider = ({
  children,
  views,
  selectedView,
  createView,
  updateView,
  deleteView,
  selectView,
  reorderViews,
  onToggleFavorite,
  hasUnsavedChanges,
  saveView,
  discardChanges,
  activeFilterConfigIds,
}: ViewsProviderProps) => {
  return (
    <ViewsContext.Provider
      value={{
        views,
        selectedView,
        createView,
        updateView,
        deleteView,
        selectView,
        reorderViews,
        onToggleFavorite,
        hasUnsavedChanges,
        saveView,
        discardChanges,
        activeFilterConfigIds,
      }}
    >
      {children}
    </ViewsContext.Provider>
  );
};
